"use client"

import type { ReactNode } from "react"
import { Lock } from "lucide-react"
import { useTranslations } from "next-intl"
import { usePatreonMode } from "@/lib/use-patreon-mode"

// The "waiting for a payment processor" screen (docs/PATREON_PLAN_C.md). Shown instead of anything that needs a payment processor while
// Patreon mode is ON. Temporary by design: the wording says so, and nothing underneath is removed.
const ART = "/screens/processor-locked/glym-farewells-upscaled-4x.webp"

type Variant = "page" | "inline"

export function ProcessorLockedScreen({
  feature,
  variant = "page",
  action,
}: {
  /** What is locked, in a few words, e.g. "Buying Goop" or "Commissions". */
  feature?: string
  variant?: Variant
  /** Optional extra content under the message (the "Support on Patreon" button goes here). */
  action?: ReactNode
}) {
  const t = useTranslations("processorLocked")
  const title = feature ? t("titleFeature", { feature }) : t("titleGeneric")
  if (variant === "inline") {
    return (
      <div role="status" className="flex items-center gap-3 rounded-lg border border-dashed bg-muted/40 px-4 py-3 text-sm">
        <Lock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0">
          <p className="font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{t("inlineBody")}</p>
        </div>
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>
    )
  }
  return (
    <div role="status" className="mx-auto flex w-full max-w-md flex-col items-center gap-4 px-4 py-10 text-center">
      <img src={ART} alt="" className="w-full max-w-xs rounded-2xl" draggable={false} />
      <div className="flex items-center gap-2 text-lg font-semibold">
        <Lock className="h-5 w-5" aria-hidden />
        <span>{title}</span>
      </div>
      <p className="text-sm text-muted-foreground">{t("body")}</p>
      {action}
    </div>
  )
}

/** Wrap anything that needs a payment processor: renders the locked screen instead of the children while Patreon mode is ON. */
export function ProcessorLockGate({
  feature,
  variant = "page",
  action,
  children,
}: {
  feature?: string
  variant?: Variant
  action?: ReactNode
  children: ReactNode
}) {
  const { enabled, loading } = usePatreonMode()
  if (loading) return null
  if (enabled) return <ProcessorLockedScreen feature={feature} variant={variant} action={action} />
  return <>{children}</>
}
